import { useState, useEffect } from 'react';
import { Sparkles, Check, X, CheckCircle, XCircle, AlertCircle, Edit, RefreshCw } from 'lucide-react';
import { supabase, Transaction, Category } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useSmartCategorizationV2 } from '../hooks/useSmartCategorizationV2';
import { useFiscalYear } from '../hooks/useFiscalYear';
import { autoAllocateToBudget } from '../utils/autoAllocation';

interface SuggestionWithTransaction {
  id: string;
  transaction_id: string;
  suggested_category_id: string;
  confidence_score: number;
  created_at: string;
  transaction: Transaction;
  category_name: string;
}

export function CategorizationTab() {
  const { effectiveUserId } = useAuth();
  const smartCategorization = useSmartCategorizationV2();
  const { startMonth, startDay } = useFiscalYear();
  const [suggestions, setSuggestions] = useState<SuggestionWithTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSuggestions, setSelectedSuggestions] = useState<Set<string>>(new Set());
  const [processingBulk, setProcessingBulk] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [editingSuggestionId, setEditingSuggestionId] = useState<string | null>(null);
  const [allocationMessage, setAllocationMessage] = useState<{ text: string; type: 'success' | 'warning' | 'error' } | null>(null);
  const [patternsCount, setPatternsCount] = useState<number>(0);
  const [uncategorizedCount, setUncategorizedCount] = useState<number>(0);

  useEffect(() => {
    loadSuggestions();
    loadCategories();
    loadStats();
  }, [effectiveUserId]);

  async function loadStats() {
    if (!effectiveUserId) return;

    try {
      // Compter les patterns d'apprentissage
      const { count: patterns } = await supabase
        .from('transaction_patterns')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', effectiveUserId);

      setPatternsCount(patterns || 0);

      // Compter les transactions non catégorisées
      const { count: uncategorized } = await supabase
        .from('transactions')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', effectiveUserId)
        .is('category_id', null);

      setUncategorizedCount(uncategorized || 0);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  }

  async function generateSuggestions() {
    if (!effectiveUserId) {
      console.error('No effectiveUserId');
      return;
    }

    setGenerating(true);
    let newSuggestionsData: any[] = [];

    try {
      console.log('1. Preparing features for user:', effectiveUserId);
      // Préparer les features des transactions (la fonction génère_suggestions_batch_v2 le fait aussi, mais on s'assure)
      try {
        const featuresCount = await smartCategorization.prepareFeatures();
        console.log('Features prepared:', featuresCount);
      } catch (featureErr) {
        console.warn('Error preparing features (non-blocking):', featureErr);
        // Non-bloquant, on continue
      }

      console.log('2. Generating suggestions via RPC...');
      // Générer les suggestions
      const { data: rpcData, error: rpcError } = await supabase.rpc('generate_suggestions_batch_v2', {
        p_user_id: effectiveUserId,
        p_limit: 100
      });

      if (rpcError) {
        console.error('RPC Error:', rpcError);
        throw new Error(`Erreur RPC: ${rpcError.message || rpcError.code || 'Erreur inconnue'}`);
      }

      console.log('3. RPC completed, result:', rpcData);

      console.log('4. Reloading suggestions from database...');
      // Recharger les suggestions
      const { data: suggestionsData, error: suggestionsError } = await supabase
        .from('categorization_suggestions')
        .select(`
          *,
          transactions (*),
          categories (name)
        `)
        .eq('user_id', effectiveUserId)
        .is('was_accepted', null)
        .order('created_at', { ascending: false });

      if (suggestionsError) {
        console.error('Error loading suggestions:', suggestionsError);
        throw new Error(`Erreur chargement: ${suggestionsError.message}`);
      }

      newSuggestionsData = suggestionsData || [];
      console.log('5. Loaded suggestions count:', newSuggestionsData.length);

      // Formatter et mettre à jour le state
      const formattedSuggestions: SuggestionWithTransaction[] = newSuggestionsData.map((item: any) => ({
        id: item.id,
        transaction_id: item.transaction_id,
        suggested_category_id: item.suggested_category_id,
        confidence_score: item.confidence_score,
        created_at: item.created_at,
        transaction: item.transactions,
        category_name: item.categories?.name || 'Catégorie inconnue'
      }));

      setSuggestions(formattedSuggestions);

      // Recharger les stats
      await loadStats();

      // Message de résultat
      if (formattedSuggestions.length > 0) {
        setAllocationMessage({
          text: `${formattedSuggestions.length} suggestion(s) générée(s) avec succès !`,
          type: 'success'
        });
      } else {
        // Vérifier si c'est un problème de manque de patterns ou de confiance trop basse
        if (patternsCount === 0) {
          setAllocationMessage({
            text: 'Aucune suggestion générée : pas de patterns d\'apprentissage. Catégorisez d\'abord quelques transactions manuellement dans l\'onglet Transactions.',
            type: 'warning'
          });
        } else {
          setAllocationMessage({
            text: `Aucune suggestion avec un niveau de confiance suffisant (≥70%). Le système a ${patternsCount} patterns mais aucune correspondance forte. Catégorisez plus de transactions similaires.`,
            type: 'warning'
          });
        }
      }
      setTimeout(() => setAllocationMessage(null), 6000);
    } catch (err) {
      console.error('=== ERROR GENERATING SUGGESTIONS ===');
      console.error('Error object:', err);
      console.error('Error type:', typeof err);
      console.error('Error constructor:', err?.constructor?.name);
      if (err instanceof Error) {
        console.error('Error message:', err.message);
        console.error('Error stack:', err.stack);
      }

      const errorMessage = err instanceof Error ? err.message : (typeof err === 'object' && err !== null ? JSON.stringify(err) : String(err));
      setAllocationMessage({
        text: `Erreur: ${errorMessage}`,
        type: 'error'
      });
      setTimeout(() => setAllocationMessage(null), 6000);
    } finally {
      setGenerating(false);
    }
  }

  async function loadCategories() {
    if (!effectiveUserId) return;

    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('user_id', effectiveUserId)
        .order('name');

      if (!error && data) {
        setCategories(data);
      }
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  }

  async function loadSuggestions() {
    if (!effectiveUserId) return;

    setLoading(true);
    try {
      console.log('Loading suggestions for user:', effectiveUserId);
      const { data, error } = await supabase
        .from('categorization_suggestions')
        .select(`
          *,
          transactions (*),
          categories (name)
        `)
        .eq('user_id', effectiveUserId)
        .is('was_accepted', null)
        .order('created_at', { ascending: false });

      console.log('Suggestions query result:', { data, error, count: data?.length });

      if (error) {
        console.error('Error loading suggestions:', error);
        return;
      }

      if (data) {
        const formattedSuggestions: SuggestionWithTransaction[] = data.map((item: any) => ({
          id: item.id,
          transaction_id: item.transaction_id,
          suggested_category_id: item.suggested_category_id,
          confidence_score: item.confidence_score,
          created_at: item.created_at,
          transaction: item.transactions,
          category_name: item.categories?.name || 'Catégorie inconnue'
        }));
        console.log('Formatted suggestions:', formattedSuggestions.length);
        setSuggestions(formattedSuggestions);
      }
    } catch (err) {
      console.error('Error loading suggestions:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleAccept(suggestion: SuggestionWithTransaction) {
    if (!effectiveUserId) return;

    try {
      await smartCategorization.acceptSuggestion(
        suggestion.transaction_id,
        suggestion.suggested_category_id
      );

      // Auto-allocate to budget
      if (startMonth !== null && startDay !== null) {
        const result = await autoAllocateToBudget({
          userId: effectiveUserId,
          transactionId: suggestion.transaction_id,
          categoryId: suggestion.suggested_category_id,
          amount: Number(suggestion.transaction.amount),
          fiscalYearStart: { month: startMonth, day: startDay }
        });

        if (result.success && result.message) {
          setAllocationMessage({ text: result.message, type: 'success' });
          setTimeout(() => setAllocationMessage(null), 3000);
        } else if (!result.success && result.message) {
          setAllocationMessage({ text: result.message, type: 'warning' });
          setTimeout(() => setAllocationMessage(null), 5000);
        }
      }

      setSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
      setSelectedSuggestions(prev => {
        const newSet = new Set(prev);
        newSet.delete(suggestion.id);
        return newSet;
      });
    } catch (err) {
      console.error('Error accepting suggestion:', err);
    }
  }

  async function handleReject(suggestion: SuggestionWithTransaction) {
    try {
      await smartCategorization.rejectSuggestion(
        suggestion.transaction_id,
        suggestion.suggested_category_id
      );
      setSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
      setSelectedSuggestions(prev => {
        const newSet = new Set(prev);
        newSet.delete(suggestion.id);
        return newSet;
      });
    } catch (err) {
      console.error('Error rejecting suggestion:', err);
    }
  }

  async function handleCorrect(suggestion: SuggestionWithTransaction, correctCategoryId: string) {
    if (!effectiveUserId) return;

    try {
      // Mark suggestion as rejected (it was incorrect)
      await supabase
        .from('categorization_suggestions')
        .update({
          was_accepted: false,
          feedback_at: new Date().toISOString()
        })
        .eq('transaction_id', suggestion.transaction_id);

      // Apply the correct category
      await supabase
        .from('transactions')
        .update({ category_id: correctCategoryId })
        .eq('id', suggestion.transaction_id)
        .eq('user_id', effectiveUserId);

      // Train the model with the correct categorization
      await smartCategorization.learnFromManualCategorization(
        suggestion.transaction_id,
        correctCategoryId
      );

      // Auto-allocate to budget
      if (startMonth !== null && startDay !== null) {
        const result = await autoAllocateToBudget({
          userId: effectiveUserId,
          transactionId: suggestion.transaction_id,
          categoryId: correctCategoryId,
          amount: Number(suggestion.transaction.amount),
          fiscalYearStart: { month: startMonth, day: startDay }
        });

        if (result.success && result.message) {
          setAllocationMessage({ text: result.message, type: 'success' });
          setTimeout(() => setAllocationMessage(null), 3000);
        } else if (!result.success && result.message) {
          setAllocationMessage({ text: result.message, type: 'warning' });
          setTimeout(() => setAllocationMessage(null), 5000);
        }
      }

      // Remove from suggestions list
      setSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
      setSelectedSuggestions(prev => {
        const newSet = new Set(prev);
        newSet.delete(suggestion.id);
        return newSet;
      });
      setEditingSuggestionId(null);
    } catch (err) {
      console.error('Error correcting suggestion:', err);
    }
  }

  async function handleBulkAccept() {
    if (selectedSuggestions.size === 0 || !effectiveUserId) return;

    setProcessingBulk(true);
    try {
      const selectedItems = suggestions.filter(s => selectedSuggestions.has(s.id));
      let successCount = 0;
      let missingBudgetCount = 0;

      for (const suggestion of selectedItems) {
        await smartCategorization.acceptSuggestion(
          suggestion.transaction_id,
          suggestion.suggested_category_id
        );

        // Auto-allocate to budget
        if (startMonth !== null && startDay !== null) {
          const result = await autoAllocateToBudget({
            userId: effectiveUserId,
            transactionId: suggestion.transaction_id,
            categoryId: suggestion.suggested_category_id,
            amount: Number(suggestion.transaction.amount),
            fiscalYearStart: { month: startMonth, day: startDay }
          });

          if (result.success) {
            successCount++;
          } else if (result.reason === 'no_budget_line') {
            missingBudgetCount++;
          }
        }
      }

      setSuggestions(prev => prev.filter(s => !selectedSuggestions.has(s.id)));
      setSelectedSuggestions(new Set());

      // Show summary message
      if (successCount > 0) {
        setAllocationMessage({
          text: `${successCount} transaction(s) imputée(s) automatiquement au budget`,
          type: 'success'
        });
        setTimeout(() => setAllocationMessage(null), 3000);
      }
      if (missingBudgetCount > 0) {
        setTimeout(() => {
          setAllocationMessage({
            text: `${missingBudgetCount} transaction(s) non imputée(s) : aucune ligne budgétaire correspondante`,
            type: 'warning'
          });
          setTimeout(() => setAllocationMessage(null), 5000);
        }, 3000);
      }
    } catch (err) {
      console.error('Error accepting bulk suggestions:', err);
    } finally {
      setProcessingBulk(false);
    }
  }

  async function handleBulkReject() {
    if (selectedSuggestions.size === 0) return;

    setProcessingBulk(true);
    try {
      const selectedItems = suggestions.filter(s => selectedSuggestions.has(s.id));

      for (const suggestion of selectedItems) {
        await smartCategorization.rejectSuggestion(
          suggestion.transaction_id,
          suggestion.suggested_category_id
        );
      }

      setSuggestions(prev => prev.filter(s => !selectedSuggestions.has(s.id)));
      setSelectedSuggestions(new Set());
    } catch (err) {
      console.error('Error rejecting bulk suggestions:', err);
    } finally {
      setProcessingBulk(false);
    }
  }

  function toggleSelection(suggestionId: string) {
    setSelectedSuggestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(suggestionId)) {
        newSet.delete(suggestionId);
      } else {
        newSet.add(suggestionId);
      }
      return newSet;
    });
  }

  function toggleSelectAll() {
    if (selectedSuggestions.size === suggestions.length) {
      setSelectedSuggestions(new Set());
    } else {
      setSelectedSuggestions(new Set(suggestions.map(s => s.id)));
    }
  }

  const allSelected = suggestions.length > 0 && selectedSuggestions.size === suggestions.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (suggestions.length === 0) {
    return (
      <div className="space-y-6">
        {allocationMessage && (
          <div className={`rounded-lg p-4 ${
            allocationMessage.type === 'success' ? 'bg-green-50 border border-green-200' :
            allocationMessage.type === 'warning' ? 'bg-yellow-50 border border-yellow-200' :
            'bg-red-50 border border-red-200'
          }`}>
            <p className={`text-sm font-medium ${
              allocationMessage.type === 'success' ? 'text-green-800' :
              allocationMessage.type === 'warning' ? 'text-yellow-800' :
              'text-red-800'
            }`}>
              {allocationMessage.text}
            </p>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <Sparkles size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Aucune suggestion en attente
          </h3>

          {patternsCount === 0 ? (
            <>
              <p className="text-gray-600 max-w-md mx-auto mb-6">
                Le système d'apprentissage automatique a besoin de données pour fonctionner.
                Commencez par catégoriser manuellement quelques transactions dans l'onglet <strong>Transactions</strong>.
              </p>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 max-w-lg mx-auto">
                <p className="text-sm text-amber-900 font-medium mb-2">
                  🎯 Comment démarrer la catégorisation automatique
                </p>
                <ul className="text-xs text-amber-800 text-left space-y-2">
                  <li><strong>1. Catégorisez manuellement 10-15 transactions</strong> dans l'onglet Transactions (cliquez sur une transaction pour lui attribuer une catégorie)</li>
                  <li><strong>2. Variez les types de transactions</strong> : dépenses récurrentes (abonnements, loyers), achats ponctuels, virements, etc.</li>
                  <li><strong>3. Revenez ici et cliquez sur "Générer des suggestions"</strong></li>
                  <li><strong>4. Le système apprendra</strong> à reconnaître automatiquement les marchands, mots-clés et montants similaires</li>
                </ul>
              </div>

              <div className="mt-4 text-sm text-gray-500">
                📊 État actuel : <strong>{uncategorizedCount} transactions non catégorisées</strong> • <strong>{patternsCount} patterns appris</strong>
              </div>
            </>
          ) : (
            <>
              <p className="text-gray-600 max-w-md mx-auto mb-4">
                Le système peut analyser vos {uncategorizedCount} transaction{uncategorizedCount > 1 ? 's' : ''} non catégorisée{uncategorizedCount > 1 ? 's' : ''} et proposer des catégories automatiquement.
              </p>

              <button
                onClick={generateSuggestions}
                disabled={generating}
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-6"
              >
                <RefreshCw size={20} className={generating ? 'animate-spin' : ''} />
                {generating ? 'Génération en cours...' : 'Générer des suggestions'}
              </button>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-lg mx-auto">
                <p className="text-sm text-blue-900 font-medium mb-2">
                  💡 Comment fonctionne le système
                </p>
                <ul className="text-xs text-blue-800 text-left space-y-1">
                  <li>• Le système a appris <strong>{patternsCount} patterns</strong> de vos catégorisations précédentes</li>
                  <li>• Il détecte automatiquement les marchands/contreparties, mots-clés et montants similaires</li>
                  <li>• Seules les suggestions avec un score de confiance ≥ 70% sont affichées</li>
                  <li>• Plus vous catégorisez, plus le système devient précis</li>
                </ul>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {allocationMessage && (
        <div className={`rounded-lg p-4 ${
          allocationMessage.type === 'success' ? 'bg-green-50 border border-green-200' :
          allocationMessage.type === 'warning' ? 'bg-yellow-50 border border-yellow-200' :
          'bg-red-50 border border-red-200'
        }`}>
          <p className={`text-sm font-medium ${
            allocationMessage.type === 'success' ? 'text-green-800' :
            allocationMessage.type === 'warning' ? 'text-yellow-800' :
            'text-red-800'
          }`}>
            {allocationMessage.text}
          </p>
        </div>
      )}

      <div className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start gap-3 mb-4">
          <Sparkles size={24} className="text-blue-600 mt-1" />
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Suggestions de catégorisation intelligente
            </h2>
            <p className="text-sm text-gray-700 mb-2">
              Le système a analysé {suggestions.length} transaction{suggestions.length > 1 ? 's' : ''} et propose des catégories basées sur l'analyse des libellés et vos catégorisations précédentes.
            </p>
            <p className="text-xs text-gray-600 mt-1">
              💡 Le système apprend de vos validations et corrections. Plus vous catégorisez de transactions similaires, plus les suggestions futures seront précises.
            </p>
          </div>
          <button
            onClick={generateSuggestions}
            disabled={generating}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            title="Générer de nouvelles suggestions pour les transactions non catégorisées"
          >
            <RefreshCw size={18} className={generating ? 'animate-spin' : ''} />
            {generating ? 'Génération...' : 'Actualiser'}
          </button>
        </div>

        {selectedSuggestions.size > 0 && (
          <div className="flex items-center gap-3 mt-4 pt-4 border-t border-blue-200">
            <span className="text-sm font-medium text-gray-900">
              {selectedSuggestions.size} sélectionnée{selectedSuggestions.size > 1 ? 's' : ''}
            </span>
            <div className="flex gap-2 ml-auto">
              <button
                onClick={handleBulkAccept}
                disabled={processingBulk}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                <CheckCircle size={18} />
                Accepter la sélection
              </button>
              <button
                onClick={handleBulkReject}
                disabled={processingBulk}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                <XCircle size={18} />
                Rejeter la sélection
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Libellé
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Montant
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Catégorie suggérée
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Confiance
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {suggestions.map((suggestion) => {
              const amount = Number(suggestion.transaction.amount);
              const confidencePercent = Math.round(suggestion.confidence_score * 100);
              const confidenceColor =
                confidencePercent >= 70 ? 'text-green-600' :
                confidencePercent >= 40 ? 'text-blue-600' :
                'text-orange-600';
              const confidenceBarColor =
                confidencePercent >= 70 ? 'bg-green-500' :
                confidencePercent >= 40 ? 'bg-blue-500' :
                'bg-orange-500';

              return (
                <tr key={suggestion.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedSuggestions.has(suggestion.id)}
                      onChange={() => toggleSelection(suggestion.id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    {new Date(suggestion.transaction.date).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {suggestion.transaction.label}
                  </td>
                  <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                    <span className={amount >= 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                      {amount.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Sparkles size={14} className="text-blue-600" />
                      <span className="text-sm font-medium text-blue-700">
                        {suggestion.category_name}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1 min-w-[100px]">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-xs font-semibold ${confidenceColor}`}>
                          {confidencePercent}%
                        </span>
                        {confidencePercent < 40 && (
                          <AlertCircle size={14} className="text-orange-500" title="Confiance faible - Vérifiez attentivement" />
                        )}
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full ${confidenceBarColor} transition-all`}
                          style={{ width: `${confidencePercent}%` }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {editingSuggestionId === suggestion.id ? (
                        <div className="flex items-center gap-2">
                          <select
                            onChange={(e) => {
                              if (e.target.value) {
                                handleCorrect(suggestion, e.target.value);
                              }
                            }}
                            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            defaultValue=""
                          >
                            <option value="" disabled>Choisir une catégorie</option>
                            {categories.map((cat) => (
                              <option key={cat.id} value={cat.id}>
                                {cat.name}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={() => setEditingSuggestionId(null)}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Annuler"
                          >
                            <X size={18} />
                          </button>
                        </div>
                      ) : (
                        <>
                          <button
                            onClick={() => handleAccept(suggestion)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Accepter"
                          >
                            <Check size={18} />
                          </button>
                          <button
                            onClick={() => setEditingSuggestionId(suggestion.id)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Modifier (corriger la catégorie)"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => handleReject(suggestion)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Rejeter"
                          >
                            <X size={18} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">À propos de la catégorisation automatique</p>
            <ul className="list-disc list-inside space-y-1 text-blue-700">
              <li>Seules les catégories présentes dans votre budget prévisionnel sont suggérées</li>
              <li>Le score de confiance indique la certitude du système pour chaque suggestion</li>
              <li>Cliquez sur <Edit size={14} className="inline" /> pour corriger une suggestion avec la bonne catégorie et entraîner le système</li>
              <li>En acceptant, rejetant ou corrigeant des suggestions, vous améliorez les futures prédictions</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
